import { Input } from '@sjuni/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import { redirectTarget } from '@/lib/auth/redirectTarget';
import { isAuthenticated, pendingStep } from '@/lib/auth/session';
import { useAuth } from '@/lib/auth/useAuth';

/**
 * docs/04-API-SPEC.md §5 — `POST /admin/auth/login`.
 *
 * The response decides where this goes next, and there are three answers, not
 * two: a full token, a TOTP challenge, or a Super Admin who has never enrolled
 * and must before they can reach anything.
 *
 * The server answers "no such account", "wrong password" and "deactivated" with
 * one message on purpose — for a university whose staff directory is public, a
 * login form that distinguishes them is an oracle for which addresses hold
 * accounts. So this screen must not try to be more helpful than that.
 */
interface LoginFields {
  email: string;
  password: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, signIn } = useAuth();

  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFields>({ defaultValues: { email: '', password: '' } });

  // Already signed in, or half-way through the flow — either way this screen
  // is not where they belong.
  if (isAuthenticated(session)) {
    return <Navigate to={redirectTarget(location)} replace />;
  }

  const step = pendingStep(session);

  if (step !== null) {
    return <Navigate to={step} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      const status = await signIn(values);

      switch (status) {
        case 'two_factor_required':
          navigate('/login/two-factor', { state: location.state as unknown });
          break;
        case 'two_factor_setup_required':
          navigate('/login/setup', { state: location.state as unknown });
          break;
        default:
          navigate(redirectTarget(location), { replace: true });
      }
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, ['email', 'password']);

      // No generic banner when a field already carries the reason — §5 answers
      // a wrong password by rejecting the address, and saying it twice reads as
      // two separate failures.
      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      <FormError message={formError} />

      <Input
        {...register('email', { required: t('auth.validation.emailRequired') })}
        type="email"
        autoComplete="username"
        /* eslint-disable-next-line jsx-a11y/no-autofocus -- a screen whose only
           purpose is this one form; the rule guards against stealing focus
           mid-page, which is not what this is. */
        autoFocus
        label={t('auth.fields.email')}
        error={errors.email?.message}
        required
      />

      <Input
        {...register('password', { required: t('auth.validation.passwordRequired') })}
        type="password"
        autoComplete="current-password"
        label={t('auth.fields.password')}
        error={errors.password?.message}
        required
      />

      <SubmitButton fullWidth loading={isSubmitting}>
        {t('auth.login.submit')}
      </SubmitButton>

      <p className="text-center text-body-sm">
        <Link to="/forgot-password" className="text-link underline-offset-2 hover:underline">
          {t('auth.login.forgot')}
        </Link>
      </p>
    </form>
  );
}
